import React, { useRef } from 'react';
import { COLORS, TYPE, WEIGHT, LETTER, SPACE } from '../styles/tokens';
import GlassCard from './ui/GlassCard';
import IconBadge from './ui/IconBadge';
import SectionHeading from './ui/SectionHeading';
import useScrollReveal from '../hooks/useScrollReveal';
import { useT } from '../i18n/I18nContext';
import { useTrackSection } from '../hooks/useTrackSection';

const STEP_ICONS: React.ReactNode[] = [
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <path d="M20 14H14l-2 3-2-3H4a2 2 0 01-2-2V5a2 2 0 012-2h16a2 2 0 012 2v7a2 2 0 01-2 2z"
          stroke="white" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M8 9h8M8 12h5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
  </svg>,
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="2.5" fill="white" opacity="0.9" />
    <circle cx="5" cy="5.5" r="2" fill="white" opacity="0.7" />
    <circle cx="19" cy="5.5" r="2" fill="white" opacity="0.7" />
    <circle cx="12" cy="20" r="2" fill="white" opacity="0.7" />
    <line x1="12" y1="9.5" x2="6.4" y2="7" stroke="white" strokeWidth="1.2" opacity="0.65" />
    <line x1="12" y1="9.5" x2="17.6" y2="7" stroke="white" strokeWidth="1.2" opacity="0.65" />
    <line x1="12" y1="14.5" x2="12" y2="18" stroke="white" strokeWidth="1.2" opacity="0.65" />
  </svg>,
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="3" width="18" height="18" rx="3" stroke="white" strokeWidth="1.7" />
    <path d="M8 9h8M8 12h8M8 15h5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M17 15l2 2-2 2" stroke="white" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>,
];
const STEP_TINTS = [COLORS.tintPink, COLORS.tintPurple, COLORS.tintBlue];
const STEP_GLOWS = [COLORS.glowPink, COLORS.glowPurple, COLORS.glowBlue];

export default function HowItWorksSection() {
  const t = useT();
  const sectionRef = useRef<HTMLElement>(null);
  const headRef  = useScrollReveal<HTMLDivElement>({ fromY: 26, duration: 0.7, start: 'top 80%' });
  const stepsRef = useScrollReveal<HTMLDivElement>({
    fromY: 24, fromScale: 0.97, duration: 0.6, stagger: 0.12, start: 'top 82%',
    selector: '.step-card',
  });
  useTrackSection('how_it_works', sectionRef);

  return (
    <section
      ref={sectionRef}
      id="how-it-works"
      style={{
        position: 'relative',
        padding: `${SPACE.sectionY} ${SPACE.sectionX}`,
        zIndex: 1,
        background: 'transparent',
      }}
    >
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div ref={headRef} style={{ marginBottom: 'clamp(32px, 4vw, 52px)', opacity: 0 }}>
          <SectionHeading title={t.howItWorks.titleA} secondLine={t.howItWorks.titleB} />
        </div>

        <div
          ref={stepsRef}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: SPACE.gridGap,
            position: 'relative',
          }}
          className="steps-grid"
        >
          {/* Decorative connector line behind the row of step cards */}
          <div className="steps-connector" style={{
            position: 'absolute',
            top: 34,
            left: 'calc(16.66% + 22px)',
            right: 'calc(16.66% + 22px)',
            height: 1,
            background: 'linear-gradient(90deg, rgba(243,107,175,0.35), rgba(139,124,255,0.35), rgba(158,216,255,0.35))',
            pointerEvents: 'none',
          }} />

          {t.howItWorks.steps.map((step, i) => (
            <GlassCard
              key={i}
              hoverGlow={STEP_GLOWS[i]}
              className="step-card"
              padding="clamp(20px, 2.5vw, 28px)"
            >
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                marginBottom: 20,
              }}>
                <IconBadge tint={STEP_TINTS[i]} size={48} radius={15}>{STEP_ICONS[i]}</IconBadge>
                <span style={{
                  fontSize: 13,
                  fontWeight: WEIGHT.bold,
                  color: 'rgba(255,255,255,0.30)',
                  letterSpacing: '0.04em',
                }}>
                  {step.n}
                </span>
              </div>
              <h3 style={{
                fontSize: TYPE.cardTitle,
                fontWeight: WEIGHT.semibold,
                color: COLORS.textPrimary,
                marginBottom: 10,
                lineHeight: 1.35,
                letterSpacing: LETTER.tight,
              }}>
                {step.title}
              </h3>
              <p style={{
                fontSize: TYPE.cardText,
                color: COLORS.textBody,
                lineHeight: 1.65,
                fontWeight: WEIGHT.regular,
              }}>
                {step.text}
              </p>
            </GlassCard>
          ))}
        </div>

        {/* Flow hint */}
        <div style={{
          textAlign: 'center',
          marginTop: 'clamp(20px, 2.5vw, 28px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexWrap: 'wrap',
          gap: 10,
          rowGap: 6,
        }}>
          {[t.howItWorks.flow[0], '→', t.howItWorks.flow[1], '→', t.howItWorks.flow[2]].map((item, i) => (
            <span key={i} style={{
              fontSize: i % 2 === 0 ? 13 : 14,
              fontWeight: i % 2 === 0 ? WEIGHT.medium : WEIGHT.regular,
              color: i % 2 === 0 ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.28)',
              letterSpacing: i % 2 === 0 ? LETTER.tight : '0',
            }}>
              {item}
            </span>
          ))}
        </div>
      </div>

      <style>{`
        @media (max-width: 640px) {
          .steps-grid { grid-template-columns: 1fr !important; }
          .steps-connector { display: none !important; }
        }
      `}</style>
    </section>
  );
}
